import { z } from 'zod';
import { isoDateSchema } from './common.js';

/**
 * Espejo de los enums de Prisma.
 *
 * Se repiten a mano a propósito: si mañana agregas un valor en el schema de
 * Prisma, este sitio es el que te obliga a decidir si el frontend lo soporta.
 * Importarlos de Prisma obligaría al navegador a cargar tipos de servidor.
 */
export const roleSchema = z.enum(['STUDENT', 'ADMIN']);
export const lessonStatusSchema = z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED']);
export const reviewKindSchema = z.enum(['EXERCISE', 'QUIZ']);
export const reviewRatingSchema = z.enum(['AGAIN', 'HARD', 'GOOD', 'EASY']);
export const bookmarkKindSchema = z.enum(['LESSON', 'GLOSSARY', 'CHEATSHEET', 'ERROR']);
export const projectStatusSchema = z.enum(['IDEA', 'PLANNING', 'IN_PROGRESS', 'DONE']);

/**
 * Límite de 72 BYTES de bcrypt. Si la contraseña es más larga, bcrypt la
 * trunca en silencio: dos contraseñas distintas con el mismo prefijo de 72
 * bytes serían equivalentes. Por eso validamos bytes, no caracteres.
 */
const utf8Length = (value) => {
  // `for...of` recorre puntos de código, no unidades UTF-16, así que un emoji
  // cuenta como un carácter y no como dos.
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    bytes += codePoint < 0x80 ? 1 : codePoint < 0x800 ? 2 : codePoint < 0x10000 ? 3 : 4;
  }
  return bytes;
};

const passwordSchema = z
  .string()
  .min(8, 'La contraseña debe tener al menos 8 caracteres')
  .refine((value) => utf8Length(value) <= 72, {
    message: 'La contraseña no puede superar 72 bytes',
  });

// ─── Requests ───────────────────────────────────────────────────────────────

export const registerSchema = z.object({
  email: z
    .email('Debe ser un correo válido')
    .max(254, 'El correo es demasiado largo')
    .trim()
    .toLowerCase(),
  name: z.string().trim().min(2, 'El nombre debe tener al menos 2 caracteres').max(80),
  password: passwordSchema,
});

export const loginSchema = z.object({
  email: z.email('Debe ser un correo válido').trim().toLowerCase(),
  // Sin validación de longitud aquí: si la contraseña es corta igual hay que
  // comprobar contra el hash, y un mensaje distinto filtraría información.
  password: z.string().min(1, 'La contraseña es obligatoria'),
});

export const updateProfileSchema = z
  .object({
    name: z.string().trim().min(2).max(80).optional(),
    timezone: z.string().trim().min(1).max(64).optional(),
    weeklyGoalMinutes: z.number().int().min(30).max(40_320).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Envía al menos un campo para actualizar',
  });

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: passwordSchema,
});

/**
 * Solo pide el correo. La API responde lo mismo exista la cuenta o no, para no
 * convertirse en un oráculo que enumera qué correos tienen cuenta.
 */
export const forgotPasswordSchema = z.object({
  email: z.email('Debe ser un correo válido').trim().toLowerCase(),
});

/**
 * El código que llegó al correo más la contraseña nueva.
 *
 * El código es de 6 dígitos a propósito: es un código corto (generado y
 * enviado por nosotros), no una contraseña que el usuario recuerde. Que sea
 * corto se compensa con límite de intentos por código y rate limit en la ruta.
 */
export const resetPasswordSchema = z.object({
  email: z.email('Debe ser un correo válido').trim().toLowerCase(),
  code: z.string().regex(/^\d{6}$/, 'El código debe tener 6 dígitos'),
  newPassword: passwordSchema,
});

// ─── Responses ──────────────────────────────────────────────────────────────

/**
 * @typedef {object} PublicUser
 * @property {string} id
 * @property {string} email
 * @property {'STUDENT' | 'ADMIN'} role
 * @property {string} name
 * @property {string} timezone
 * @property {number} weeklyGoalMinutes
 * @property {string} createdDate ISO 8601.
 */

/** El usuario tal como viaja por la API. Nunca incluye passwordHash. */
export const publicUserSchema = z.object({
  id: z.string(),
  email: z.email(),
  name: z.string(),
  role: roleSchema,
  timezone: z.string(),
  weeklyGoalMinutes: z.number().int(),
  createdAt: isoDateSchema,
});
